import { Box, Typography, Container, Grid, Paper, Avatar } from '@mui/material';
import { motion, useInView } from 'framer-motion';
import { Visibility, LockOpen, Public, TrendingUp, Lightbulb, Rocket, Heart, Shield } from '@mui/icons-material';
import { useRef } from 'react';

const VisionStatement = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const pillars = [
    {
      icon: <Visibility sx={{ fontSize: 50 }} />,
      title: 'Visionary Leadership',
      description: 'Anticipating global trends and positioning for future success with strategic foresight.',
      gradient: 'linear-gradient(135deg, #C8A96B, #e0c48a)',
    },
    {
      icon: <LockOpen sx={{ fontSize: 50 }} />,
      title: 'Unrestricted Access',
      description: 'Eliminating barriers between leadership and opportunity for direct communication.',
      gradient: 'linear-gradient(135deg, #C8A96B, #e0c48a)',
    },
    {
      icon: <Public sx={{ fontSize: 50 }} />,
      title: 'Global Connectivity',
      description: 'Building bridges across markets and cultures worldwide for sustainable partnerships.',
      gradient: 'linear-gradient(135deg, #C8A96B, #e0c48a)',
    },
    {
      icon: <TrendingUp sx={{ fontSize: 50 }} />,
      title: 'Sustainable Growth',
      description: 'Creating lasting value for partners and communities through ethical practices.',
      gradient: 'linear-gradient(135deg, #C8A96B, #e0c48a)',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.12, duration: 0.8 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.8, rotateY: -15 },
    visible: { opacity: 1, scale: 1, rotateY: 0, transition: { duration: 0.5 } },
    hover: { scale: 1.08, y: -12, transition: { duration: 0.25, type: "spring", stiffness: 300 } },
  };

  return (
    <Box
      ref={ref}
      sx={{
        py: { xs: 10, md: 18 },
        background: 'linear-gradient(135deg, #050505 0%, #0a0a0a 50%, #0f0f0f 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-20%',
          left: '-10%',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(200,169,107,0.03) 0%, transparent 70%)',
          borderRadius: '50%',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '-20%',
          right: '-10%',
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(200,169,107,0.02) 0%, transparent 70%)',
          borderRadius: '50%',
        },
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={containerVariants}
        >
          {/* Header Section */}
          <motion.div variants={itemVariants}>
            <Typography
              variant="subtitle1"
              sx={{
                color: '#C8A96B',
                letterSpacing: 6,
                mb: 2,
                textAlign: 'center',
                fontSize: '0.85rem',
                fontWeight: 600,
                textTransform: 'uppercase',
              }}
            >
              Our Philosophy
            </Typography>

            <Typography
              variant="h2"
              sx={{
                textAlign: 'center',
                mb: 4,
                fontSize: { xs: '2.2rem', sm: '3rem', md: '3.8rem' },
                fontWeight: 800,
                background: 'linear-gradient(135deg, #fff 0%, #e0e0e0 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              The Vision
            </Typography>

            {/* Quote Box */}
            <Box
              sx={{
                maxWidth: '850px',
                mx: 'auto',
                mb: 10,
                position: 'relative',
                '&::before': {
                  content: '"\\u201C"',
                  position: 'absolute',
                  top: -40,
                  left: -20,
                  fontSize: '6rem',
                  color: 'rgba(200,169,107,0.15)',
                  fontFamily: 'serif',
                },
                '&::after': {
                  content: '"\\u201D"',
                  position: 'absolute',
                  bottom: -60,
                  right: -20,
                  fontSize: '6rem',
                  color: 'rgba(200,169,107,0.15)',
                  fontFamily: 'serif',
                },
              }}
            >
              <Typography
                sx={{
                  textAlign: 'center',
                  fontSize: { xs: '1.2rem', md: '1.5rem' },
                  lineHeight: 1.6,
                  color: '#C8A96B',
                  fontStyle: 'italic',
                  fontWeight: 400,
                  letterSpacing: 0.5,
                }}
              >
                "Direct communication creates direct opportunity. No barriers,
                no gatekeepers, just access to leadership."
              </Typography>
              <Typography
                sx={{
                  textAlign: 'center',
                  mt: 2,
                  color: '#777',
                  fontSize: '0.9rem',
                  letterSpacing: 2,
                }}
              >
                — JOHN PIERRE ASFAR
              </Typography>
            </Box>
          </motion.div>

          {/* Pillars Grid */}
          <Grid container spacing={4}>
            {pillars.map((pillar, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <motion.div
                  variants={cardVariants}
                  initial="hidden"
                  animate={isInView ? "visible" : "hidden"}
                  whileHover="hover"
                  transition={{ delay: index * 0.1 }}
                >
                  <Paper
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      background: 'linear-gradient(135deg, #0f0f0f 0%, #121212 100%)',
                      height: '100%',
                      borderRadius: '24px',
                      border: '1px solid rgba(200,169,107,0.15)',
                      transition: 'all 0.3s',
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: pillar.gradient,
                        transform: 'scaleX(0)',
                        transition: 'transform 0.3s ease',
                      },
                      '&:hover::before': {
                        transform: 'scaleX(1)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        color: '#C8A96B',
                        mb: 2.5,
                        display: 'flex',
                        justifyContent: 'center',
                        transition: 'transform 0.3s',
                        '&:hover': {
                          transform: 'scale(1.1) rotateY(180deg)',
                        },
                      }}
                    >
                      {pillar.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        fontWeight: 700,
                        color: '#fff',
                        fontSize: '1.1rem',
                      }}
                    >
                      {pillar.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#aaa',
                        lineHeight: 1.7,
                        fontSize: '0.85rem',
                      }}
                    >
                      {pillar.description}
                    </Typography>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* Bottom CTA */}
          <motion.div variants={itemVariants}>
            <Box
              sx={{
                mt: 10,
                textAlign: 'center',
                p: { xs: 3, md: 5 },
                background: 'rgba(200,169,107,0.03)',
                borderRadius: '20px',
                border: '1px solid rgba(200,169,107,0.1)',
              }}
            >
              <Typography variant="h6" sx={{ color: '#C8A96B', mb: 1, fontWeight: 600 }}>
                Join the Movement
              </Typography>
              <Typography sx={{ color: '#aaa', maxWidth: '600px', mx: 'auto' }}>
                Be part of a new era in executive leadership where accessibility meets opportunity.
              </Typography>
            </Box>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

export default VisionStatement;